//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { IPromise } from 'angular';
import _ from 'underscore';

import { getRightsAutomationConfigurations } from '@account-settings/redux/rights-management/automateRightsRequestingSlice';
import TUniversalImageActionsApiService from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import IItemMetadata from '@models/IItemMetadata';
import PartialCuid from '@models/PartialCuid';
import ReelId from '@models/ReelId';
import { RightsCreationSource } from '@models/RightsCreationSource';
import { approveForGalleriesActions } from '@uia/approve-for-galleries/approve-for-galleries-slice';
import { setUIAModalOpen } from '@uia/uia-modal/modal-slice';
import { DateTimeService } from '@services/date-time-service';
import { getMediaTrackingHandler } from '@tracker/mediaTracking/mediaTrackingHandler';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { IStateService } from 'angular-ui-router';
import { isDefined } from '@utils/filterPredicates';
import { selectedItemActions } from '@uia/selected-item/selected-item-actions';
import { getRightsCapabilitiesMultiHandle } from '@apis/rights-management/rightsManagementApi';
import { requestMultiHandleRightsGraphApi } from 'app/bulk-actions/universal-image-actions-api';

const curalate = angular.module('curalate');

/**
 * UniversalImageActionsService
 *
 * Maintains state of the modal and enables intercommunication between the "curalate" app context and the
 * "uia" app context.
 *
 * Uses $window to share state.
 */
curalate.service('UniversalImageActionsService', [
  '$location',
  '$modal',
  '$ngRedux',
  '$q',
  '$rootScope',
  '$state',
  '$window',
  'ContentLibraryItemDetailsService',
  'DateTimeService',
  'ErrorNotificationService',
  'FanreelService',
  'InstagramSettingsService',
  'ItemLaunchOptions',
  'RightsRequestActions',
  'RightsStatus',
  'rx',
  'SelectedItemActions',
  'SessionVarsService',
  'ToastNotificationService',
  'TrackingService',
  'UniversalImageActionsWorkflowActions',
  'UniversalImageActionsApiService',
  'UniversalImageActionsHelpers',
  'UniversalImageActionsView',
  'UniversalItemId',
  function (
    $location,
    $modal,
    $ngRedux,
    $q,
    $rootScope,
    $state: IStateService,
    $window,
    ContentLibraryItemDetailsService,
    DateTimeService: DateTimeService,
    ErrorNotificationService,
    FanreelService,
    InstagramSettingsService,
    ItemLaunchOptions,
    RightsRequestActions,
    RightsStatus,
    rx,
    SelectedItemActions,
    SessionVarsService,
    ToastNotificationService,
    TrackingService,
    UniversalImageActionsWorkflowActions,
    UniversalImageActionsApiService: TUniversalImageActionsApiService,
    UniversalImageActionsHelpers,
    UniversalImageActionsView,
    UniversalItemId,
  ) {
    let reelsPromise;
    let currentWorkflow = null;
    let itemMetadata: IItemMetadata;
    let itemMetadataPromise: IPromise<IItemMetadata>;
    let beautifiedImage;
    let scheduleTags = [];
    let reelTags;
    let selectedReel;
    let selectedReelMetadataPromise;
    let supportsRightsRequests = false;
    let selectedScheduleTime;
    let modalSessionStartTime;
    const isUiaPermalinksEnabled =
      SessionVarsService.isFeatureEnabled('uia_permalinks');
    const isRightsRequestsTwitterEnabled = SessionVarsService.isFeatureEnabled(
      'rights_requests_twitter',
    );
    const REEL_SHOW_VISIBILITY_HIDDEN = 10;

    const instagramRequestStatus = new rx.Subject();
    this.bypassRightsRequest = new rx.Subject();
    const automaticRightsRequestCapable = new rx.Subject();

    const startModalSession = () => {
      modalSessionStartTime = DateTimeService.userMoment();
    };

    const endModalSession = () => {
      modalSessionStartTime = null;
    };

    const getCurrentSessionDuration = () => {
      const now = DateTimeService.userMoment();

      if (!modalSessionStartTime) {
        return 0;
      }

      return now.subtract(modalSessionStartTime).seconds();
    };

    const mapStateToProps = (state) => {
      const reloadItemMetadata = state.uiaModal.selectedItem.reloadItemMetadata;

      if (reloadItemMetadata) {
        this.getItemMetadataAsync(
          reloadItemMetadata,
          {},
          true /* forceRefresh */,
        );
      }

      return {
        customRightsRequest: state.uiaModal.rightsRequest.customRightsRequest,
      };
    };

    const mapDispatchToProps = (dispatch) => {
      return {
        setCustomRightsRequestAction: (requestObj) => {
          dispatch(RightsRequestActions.setCustomRightsRequest(requestObj));
        },
        receiveItemMetadata: (newItemMetadata) => {
          dispatch(SelectedItemActions.receiveItemMetadata(newItemMetadata));
        },
        setIsUIAopen: (isUIAOpen) => {
          dispatch(setUIAModalOpen(isUIAOpen));
        },
        selectGallery: (reelId: ReelId) => {
          dispatch(approveForGalleriesActions.selectGallery(reelId));
        },
      };
    };
    this.unsubscribe = $ngRedux.connect(
      mapStateToProps,
      mapDispatchToProps,
    )(this);

    this.resetModalSessionTimer = startModalSession;

    this.views = UniversalImageActionsView;

    // Gets the UIA metadata for the specified itemId. This method is invoked immediately on
    // launch of the UIA modal, so other methods within this service may depend on itemMetadataPromise
    // being initialized in order to perform their duties. This method may be invoked without parameters
    // on subsequent calls and will implicitly return cached metadata for the first invocation.
    this.getItemMetadataAsync = (itemId: PartialCuid, params, forceRefresh) => {
      const self = this;
      if (!itemMetadataPromise || forceRefresh) {
        if (!itemId) {
          throw new Error(
            'Expected itemId to be defined on first invocation of getItemMetadataAsync.',
          );
        }

        itemMetadataPromise =
          UniversalImageActionsApiService.getItemMetadataAsync(itemId, params);
        // Runs after metadata is received to fill in `originalUrl`.
        itemMetadataPromise
          .then((metadata) => {
            this.receiveItemMetadata(metadata);
            self.setImageUrl(metadata.displayImageUrl, metadata.imageUrl);
            itemMetadata = metadata;

            const existingRightsPermissionsSource =
              itemMetadata &&
              itemMetadata.rightsPermissions &&
              itemMetadata.rightsPermissions.creationSource;

            const hasAutoApprovedRights =
              existingRightsPermissionsSource ===
                RightsCreationSource.RelationshipClientToClient ||
              existingRightsPermissionsSource ===
                RightsCreationSource.RelationshipInfluencerToClient ||
              existingRightsPermissionsSource ===
                RightsCreationSource.SkippedRightsRequest;

            reelTags = angular.copy(metadata.userTags);
            supportsRightsRequests =
              (metadata.sourceNetwork === 'Instagram' ||
                metadata.sourceNetwork === 'Facebook' ||
                metadata.sourceNetwork === 'TikTok' ||
                metadata.sourceNetwork === 'YouTube' ||
                (metadata.sourceNetwork === 'Twitter' &&
                  isRightsRequestsTwitterEnabled)) &&
              metadata.rightsRequestStatus.status !==
                RightsStatus.NotSupported &&
              metadata.rightsRequestStatus.status !== RightsStatus.BrandOwned &&
              hasAutoApprovedRights === false;
          })
          .then(() => {
            const launchOptions = this.getCurrentLaunchOptions();
            const itemId = launchOptions.getUniversalItemId();

            getRightsCapabilitiesMultiHandle({ cuid: itemId }).then((res) => {
              $ngRedux.dispatch(
                RightsRequestActions.setGraphRequestRights(res),
              );
            });
          })
          .then(() => {
            $ngRedux.dispatch(getRightsAutomationConfigurations());
          });
      }
      $ngRedux.dispatch(selectedItemActions.reloadedItemMetadata());
      return itemMetadataPromise;
    };

    this.getAutomaticRightsRequestCapable = () => {
      return automaticRightsRequestCapable;
    };

    this.getInstagramRequestStatus = () => {
      return instagramRequestStatus;
    };

    this.getOverrideParamsDiff = (originalParams, overrideParams) => {
      const allOriginalParams = _.chain(originalParams.paramGroups)
        .pluck('params')
        .flatten()
        .value();
      const allOverrideParams = _.chain(overrideParams.paramGroups)
        .pluck('params')
        .flatten()
        .value();

      return _(allOverrideParams).filter((param: any, index) => {
        return (
          param.value !== allOriginalParams[index].value ||
          param.autoSet !== allOriginalParams[index].autoSet
        );
      });
    };

    this.getCurrentLaunchOptions = () => {
      return $window.crl8CurrentLaunchOptions;
    };

    this.setCurrentLaunchOptions = (opts) => {
      $window.crl8CurrentLaunchOptions = opts;
    };

    this.getCurrentFeatureSet = () => {
      return $window.crl8CurrentfeatureSet;
    };

    this.setCurrentFeatureSet = (featureSet) => {
      $window.crl8CurrentfeatureSet = featureSet;
    };

    this.getCurrentPage = () => {
      return $window.crl8CurrentPage;
    };

    this.getNumTaggedProducts = () => {
      return $window.crl8NumTaggedProducts;
    };

    this.setNumTaggedProducts = (num) => {
      $window.crl8NumTaggedProducts = num;
      $rootScope.$broadcast('uia:num-tagged-products-changed', num);
    };

    this.setCurrentPage = (page) => {
      $window.crl8CurrentPage = page;
    };

    this.launch = (view, options) => {
      const clientId = SessionVarsService.getClientId();

      const mediaTrackingHandler = options.trackingConfig
        ? getMediaTrackingHandler(options.trackingConfig.source)
        : undefined;

      const universalItemId = new UniversalItemId(
        options.itemType.toLowerCase(),
        options.itemId,
        clientId,
      ).toString();

      const { itemId, itemType } = options;

      const itemMetadataPromise =
        UniversalImageActionsApiService.getItemMetadataAsync(
          PartialCuid.build({ itemType, id: itemId }),
          {},
        );

      const isAddLabelsEnabled = angular.isUndefined(options.isAddLabelsEnabled)
        ? true
        : options.isAddLabelsEnabled;
      const launchOptions = new ItemLaunchOptions(
        options.itemType.toLowerCase(),
        options.itemId,
        {
          socialNetwork: options.socialNetwork,
          autoPlayVideo: options.autoPlayVideo,
          isAddLabelsEnabled,
          mediaTrackingHandler,
          trackingConfig: {
            ...options.trackingConfig,
          },
        },
      );

      launchOptions.setLaunchView(view);
      launchOptions.setLaunchReel(options.selectedReel);

      if (options.workflow) {
        launchOptions.setLaunchWorkflow(options.workflow);
      }

      if (
        options.products &&
        view === UniversalImageActionsView.Productize.name
      ) {
        launchOptions.setLaunchProducts(options.products);
      }

      if (options.showroomContext) {
        launchOptions.setShowroomLaunchContext(options.showroomContext);
      }

      launchOptions.setCampaigns(options.campaigns);

      this.setCurrentLaunchOptions(launchOptions);
      this.setCurrentFeatureSet(options.features);
      this.setCurrentPage(options.page);

      if (isUiaPermalinksEnabled) {
        $location.search('openItem', universalItemId);
      }

      const modal = $modal.open({
        backdrop: false,
        keyboard: false,
        template:
          '<universal-image-actions-bootstrapper></universal-image-actions-bootstrapper>',
        windowClass: 'pure-bs3 modal-uia',
      });

      itemMetadataPromise.then((metadata) => {
        mediaTrackingHandler?.track({
          event: MediaTrackingEventName.UniversalImageActionsModalOpened,
          cuid: universalItemId,
          videoId: metadata.videoServiceId,
          fileId: metadata.fileId,
        });
      });

      startModalSession();
      this.setIsUIAopen(true);

      if (options.page && !_.isEmpty(options.page)) {
        TrackingService.recordEvent(
          UniversalImageActionsHelpers.getTrackingCategory(options.page),
          TrackingService.ACTION_OPEN_MODAL,
        );
      }

      launchOptions.close = (result) => {
        modal.close(result);
        if (isDefined($state.params.openItem)) {
          $state.go('.', { openItem: undefined });
        }
      };

      modal.result
        .then((response) => {
          const durationInSeconds = getCurrentSessionDuration();

          if (response) {
            $rootScope.$broadcast('uia:modal:closed', response);

            // Proactively broadcast the metrics refresh event for syndication from this point
            // because firing it from the UIA modal will broadcast the event on the incorrect $rootScope
            $rootScope.$broadcast('syndication:metrics:refresh');

            // Proactively refresh item details upon close of the UIA modal
            // This ensures icons are refreshed on the UIA item overlay
            const partialCuid = new UniversalItemId(
              options.itemType.toLowerCase(),
              options.itemId,
            ).toString();
            ContentLibraryItemDetailsService.requestItemDetails(
              partialCuid,
              /* force refresh */ true,
            );

            if (response.result) {
              if (
                response.result.actionCompleted &&
                !_.isEmpty(response.result.actionCompleted)
              ) {
                TrackingService.recordEvent(
                  UniversalImageActionsHelpers.getTrackingCategory(
                    options.page,
                  ),
                  response.result.actionCompleted,
                  'CompletionTime',
                  durationInSeconds,
                );
              }

              if (response.result.successToast) {
                ToastNotificationService.success(response.result.successToast);
              }

              if (response.result.error) {
                if (response.result.error.message) {
                  ErrorNotificationService.showDefaultErrorDialog(
                    response.result.error.message,
                    response.result.error,
                  );
                } else {
                  ErrorNotificationService.showDefaultErrorDialog(
                    response.result.error,
                  );
                }
              }
            }
          }
        })
        .finally(() => {
          if (isUiaPermalinksEnabled && $location.search().openItem) {
            // UIA has just been closed. If the `?openItem` param is still in the URL,
            // this signifies that we were closed via a UI button press instead of via
            // the browser's back button. In this case, we should trigger a browser back button
            // action to clean up the URL & history state.
            $window.history.back();
          }
          TrackingService.recordEvent(
            UniversalImageActionsHelpers.getTrackingCategory(options.page),
            TrackingService.ACTION_CLOSE_MODAL,
          );

          this.setIsUIAopen(false);
          endModalSession();
        });

      return modal;
    };

    //
    // Modal Session State
    //
    // Unless specifically called out, the state managed here is expected to be wiped
    // out from UIA modal launch-to-launch since the service should be a new instance
    // each time the modal launcher boots its isolated Angular application.
    //

    //
    // Workflow
    //
    this.getCurrentWorkflow = () => {
      return currentWorkflow;
    };

    this.setCurrentWorkflow = (workflow) => {
      const oldWorkflow = currentWorkflow;
      currentWorkflow = workflow;
      $ngRedux.dispatch(
        UniversalImageActionsWorkflowActions.setCurrentWorkflow(
          currentWorkflow,
          oldWorkflow,
        ),
      );

      $rootScope.$broadcast('uia:workflow-changed', workflow, oldWorkflow);
    };

    this.setImageUrl = (newDisplayUrl, newCanonicalUrl) => {
      const newImage = {
        displayImageUrl: newDisplayUrl,
        canonicalImageUrl: newCanonicalUrl,
      };

      $rootScope.$broadcast('uia:modal:image-url-changed', newImage);
    };

    this.getCanonicalImageUrl = () => {
      return (
        (beautifiedImage && beautifiedImage.imageUrl) || itemMetadata.imageUrl
      );
    };

    this.getCanonicalFileId = () => {
      return (beautifiedImage && beautifiedImage.fileId) || itemMetadata.fileId;
    };

    this.setBeautifiedImage = (newBeautifiedImage) => {
      beautifiedImage = newBeautifiedImage;
      this.setImageUrl(
        beautifiedImage.displayImageUrl,
        beautifiedImage.imageUrl,
      );
    };

    this.revertBeautifiedImage = (revertedImage) => {
      beautifiedImage = null;
      this.setImageUrl(revertedImage.displayImageUrl, revertedImage.imageUrl);
    };

    this.trackMediaEvent = (eventName) => {
      const launchOptions = this.getCurrentLaunchOptions();

      const { rootCuid, fileId, videoServiceId } =
        $ngRedux.getState().uiaModal.selectedItem.selectedItemMetadata;

      launchOptions.mediaTrackingHandler?.track({
        cuid: rootCuid,
        event: eventName,
        fileId: fileId,
        videoId: videoServiceId,
      });
    };

    //
    // Fanreels
    //
    this.getCachedReelsAsync = () => {
      const self = this;

      // The list of Reels is safe to cache since it won't normally change between
      // UIA modal launches, and if it does, the user can just refresh the page.
      if (!reelsPromise) {
        const launchOptions = self.getCurrentLaunchOptions();
        let launchReel;

        const queryParams = {
          noStats: true,
          includeModeratedCanvases: true,
        };

        // If a launchReel is set, this is probably a Fanreel moderation page, so we should be aware of CaaS reels.
        if (launchOptions) {
          launchReel = launchOptions.getLaunchReel();
          if (launchReel && launchReel.id) {
            _(queryParams).extend({
              includeCaasReels: true,
              showVisibility: REEL_SHOW_VISIBILITY_HIDDEN,
            });
          }
        }

        reelsPromise = FanreelService.getReelsAsync(queryParams);
        reelsPromise.then((reels) => {
          // If the selected reel hasn't been set yet, check the launchOptions to see if one was set
          // on initialization.
          if (!selectedReel && launchOptions && launchReel) {
            const reel = _(reels).find((r) => r.id === launchReel.id);
            self.setSelectedReel(reel);
          }
        });
      }

      return reelsPromise;
    };

    this.getScheduleTags = () => {
      return scheduleTags;
    };

    this.setScheduleTags = (newTags) => {
      scheduleTags = newTags;
    };

    this.getReelTags = () => {
      return angular.copy(reelTags);
    };

    this.setReelTags = (tags) => {
      reelTags = angular.copy(tags);
    };

    this.getOriginalReelTags = () => {
      return itemMetadata?.userTags;
    };

    this.getSelectedReel = () => {
      return selectedReel;
    };

    this.setSelectedReel = (reel) => {
      const self = this;

      if (reel && reel !== selectedReel) {
        selectedReel = reel;
        this.selectGallery(selectedReel.id);
        self.refreshReelMetadataAsync(selectedReel.id).then(() => {
          $rootScope.$broadcast(
            'uia:modal:selected-reel-changed',
            selectedReel,
          );
        });
      }
    };

    this.refreshReelMetadataAsync = (reelId) => {
      const self = this;

      selectedReelMetadataPromise = FanreelService.getReelMetadataAsync(reelId);
      $rootScope.$broadcast('uia:workflow:loading', true);

      return $q
        .all([selectedReelMetadataPromise, itemMetadataPromise])
        .then((responses) => {
          const metadata = responses[0];
          selectedReel = {
            ...selectedReel,
            ...metadata,
          };

          const workflow = self.getCurrentWorkflow();

          if (workflow) {
            if (metadata.isRightsManaged && supportsRightsRequests) {
              workflow.appendState(UniversalImageActionsView.RightsRequest);
            } else {
              workflow.removeState(UniversalImageActionsView.RightsRequest);
            }
          }
          $rootScope.$broadcast('uia:workflow:loading', false);
        });
    };

    this.getSelectedReelMetadataAsync = () => {
      const cachedReelsPromise = this.getCachedReelsAsync();

      // We can't retrieve the selected reel metadata until we've retrieved the list of reels to select from...
      const chainedPromise = cachedReelsPromise.then(() => {
        return selectedReelMetadataPromise;
      });

      return chainedPromise;
    };
    //
    // Rights Management
    //
    this.rightsRequestStatusChanged = (newVal) => {
      $rootScope.$broadcast('uia:rights-request-status-changed', newVal);
    };

    this.itemSupportsRightsRequests = () => {
      return supportsRightsRequests;
    };

    this.setCustomRightsRequest = (newVal) => {
      if (newVal) {
        this.setCustomRightsRequestAction({
          requestForRightsMessage: newVal.rightsRequestText,
          approvalPhrase: newVal.approvalPhrase,
          bypassRightsRequest: newVal.bypassRightsRequest,
        });
      } else {
        this.setCustomRightsRequestAction({
          requestForRightsMessage: null,
          approvalPhrase: null,
          bypassRightsRequest: null,
        });
      }

      $rootScope.$broadcast('uia:modal:rights-request-changed', newVal);
    };

    this.getCustomRightsRequest = () => {
      if (this.customRightsRequest) {
        return {
          rightsRequestText: this.customRightsRequest.requestForRightsMessage,
          approvalPhrase: this.customRightsRequest.approvalPhrase,
          bypassRightsRequest: this.customRightsRequest.bypassRightsRequest,
        };
      } else {
        return null;
      }
    };

    this.getRightsRequestAsync = () => {
      const deferred = $q.defer();

      itemMetadataPromise.then((metadata) => {
        if (
          metadata.rightsRequestStatus &&
          metadata.rightsRequestStatus.requestMessage
        ) {
          deferred.resolve(metadata.rightsRequestStatus);
        } else {
          deferred.resolve('none');
        }
      });

      return deferred.promise;
    };

    //
    // Schedule Now
    //
    this.getScheduledTime = () => {
      return selectedScheduleTime;
    };

    this.setScheduledTime = (scheduleTime) => {
      selectedScheduleTime = scheduleTime;

      $rootScope.$broadcast('uia:modal:schedule-time-changed', scheduleTime);
    };

    this.sendRightsRequest = (partialCuid, params, trackingCategory) => {
      const self = this;
      TrackingService.recordEvent(
        trackingCategory,
        TrackingService.ACTION_REQUESTED_RIGHTS,
        'message=direct-request',
      );

      return requestMultiHandleRightsGraphApi({
        partialCuid,
        params,
      })
        .then((result) => {
          const rightsStatus =
            typeof result === 'object' ? result.status : result;
          if (rightsStatus.toLowerCase() === RightsStatus.Pending) {
            self.rightsRequestStatusChanged(RightsStatus.Pending);
            instagramRequestStatus.next(RightsStatus.Pending);
          }
          // Refresh metadata to get updated rights request data -- prevents bug where stale data is shown
          // if user navigates away and then back to request panel after making a request.
          self.getItemMetadataAsync(
            partialCuid,
            params,
            true /* forceRefresh */,
          );
        })
        .catch((error) => {
          if (error.message) {
            ErrorNotificationService.showDefaultErrorDialog(
              error.message,
              error,
            );
          } else {
            ErrorNotificationService.showDefaultErrorDialog(error);
          }
          return true;
        });
    };

    this.initiateRightsRequest = (
      cuid,
      approvalPhrase,
      requestMessage,
      communicationType,
      requestMessageSource,
      requestMessageLanguage,
    ) => {
      const self = this;
      return UniversalImageActionsApiService.initiateItemAsync({
        cuid,
        approvalPhrase,
        requestMessage,
        communicationType,
        requestMessageSource,
        requestMessageLanguage,
      }).then((result) => {
        if (result.status.toLowerCase() === RightsStatus.Initiated) {
          self.rightsRequestStatusChanged(RightsStatus.Initiated);
          self.getInstagramRequestStatus().next(RightsStatus.Initiated);
        }
        // Refresh metadata to get updated rights request data -- prevents bug where stale data is shown
        // if user navigates away and then back to request panel after making a request.
        self.getItemMetadataAsync(
          cuid,
          {} /* params */,
          true /* forceRefresh */,
        );
      });
    };

    this.getInstagramBusinessId = () => {
      return InstagramSettingsService.getInstagramBusinessId();
    };

    this.$onDestroy = () => {
      this.unsubscribe();
    };
  },
]);
